import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../api";

interface LabInfo {
  lab_name: string | null;
  location: string | null;
  contact_no: string;
}
interface PatientInfo {
  P_name: string;
  prefix: string;
  P_code: string;
  mobile: string;
  gender?: string;
  age?: number;
  age_type?: string;
}
interface BillItem {
  item_name: string;
  amount: string | number;
}
// "single"/"group" test-category rollups returned by the backend —
// one row per selected profile/category instead of every sub-test.
interface CategoryItem {
  category_id?: number | string;
  category_name: string;
  amount: string | number;
}
interface BillData {
  billnum: string;
  bill_date: string;
  net_amount: string | number;
  home_visit: string | number;
  paid_amount: string | number;
  balance: string | number;
  payment_method: string;
  reference_no: string;
  ref_prefix: string;
  reference: string;
  patient_name?: string;
}
interface ReceiptData {
  labInfo: LabInfo;
  patientInfo: PatientInfo | null;
  billData: BillData;
  billItems: BillItem[];
  singleCategories?: CategoryItem[];
  groupCategories?: CategoryItem[];
}

// Format a date/timestamp as DD/MM/YYYY in Indian Standard Time (IST).
// "en-GB" reliably renders DD/MM/YYYY with "/" separators; the explicit
// timeZone locks it to IST regardless of the browser/server's local zone.
const formatDateIST = (value?: string | number | Date | null): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
};

const BillingPosReceipt: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [data, setData]       = useState<ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (billId) fetchReceipt(billId);
    else { setError("No bill ID found in URL"); setLoading(false); }
  }, [billId]);

  const fetchReceipt = async (id: string) => {
    try {
      const res = await API.get(`/billing/receipt/pos/${id}`);
      if (res.data?.success && res.data?.data) {
        setData(res.data.data);
      } else {
        setError("Receipt not found");
      }
    } catch (err: any) {
      setError(`Failed to load: ${err?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  };

  const n = (v: any) => parseFloat(String(v ?? 0)) || 0;

  // Merge flat items + rolled-up single/group categories into one list —
  // this is what fixes "No items found" for bills that only contain
  // single/group (profile) selections instead of flat line items.
  const mergeDisplayItems = (d: ReceiptData) => {
    const flat = (Array.isArray(d.billItems) ? d.billItems : []).map((i) => ({
      name: String(i.item_name || ""),
      amount: n(i.amount),
    }));
    const singles = (d.singleCategories || []).map((c) => ({
      name: c.category_name,
      amount: n(c.amount),
    }));
    const groups = (d.groupCategories || []).map((c) => ({
      name: c.category_name,
      amount: n(c.amount),
    }));
    return [...flat, ...singles, ...groups];
  };

  const buildReceiptData = (d: ReceiptData) => {
    const labInfo     = d.labInfo     || {};
    const patientInfo = d.patientInfo || null;
    const billData    = d.billData    || {} as BillData;

    const totalItems = mergeDisplayItems(d);

    const subTotal  = totalItems.reduce((s, i) => s + i.amount, 0);
    const netTotal  = n(billData.net_amount);
    const paid      = n(billData.paid_amount);
    const balance   = n((billData as any).balance ?? (netTotal - paid));
    const homeVisit = n(billData.home_visit);
    const balanceColor = balance > 0 ? "r-red" : "r-green";

    const itemsHtml = totalItems.map((item) =>
      `<div class="r-row"><span class="r-item-name">${item.name}</span><span class="r-item-amt">₹${item.amount.toFixed(2)}</span></div>`
    ).join("");

    const patientName = patientInfo
      ? `${patientInfo.prefix ? patientInfo.prefix + ". " : ""}${patientInfo.P_name || "—"}`
      : billData.patient_name || "—";

    return {
      labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
      homeVisit, balanceColor, itemsHtml, patientName,
    };
  };

  const handlePrint = () => {
    if (!data) return;
    const {
      labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
      homeVisit, balanceColor, itemsHtml, patientName,
    } = buildReceiptData(data);

    const formattedDate = formatDateIST(billData.bill_date);

    const printWindow = window.open("", "_blank", "width=420,height=750");
    if (!printWindow) { alert("Popup blocked! Please allow popups for this site."); return; }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>POS Receipt</title>
          <style>
            @page { size: 74mm auto; margin: 0; }
            @media print {
              html, body { width: 74mm; margin: 0 !important; padding: 0 !important; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            html, body {
              font-family: 'Courier New', 'Courier', monospace;
              font-size: 11px;
              font-weight: 700;
              color: #000;
              background: white;
              width: 74mm;
              margin: 0 auto;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .receipt-wrap { width: 74mm; padding: 3mm 3mm; margin-bottom: 10px; }
            .r-center { text-align: center; }
            .r-h3 { font-size: 14px; font-weight: 900; text-transform: uppercase; margin-bottom: 2px; letter-spacing: 0.3px; }
            .r-h4 { font-size: 11px; font-weight: 700; color: #000; margin-bottom: 1px; }
            .r-hr { border: none; border-top: 1.5px dashed #000; margin: 5px 0; }
            .r-row { display: flex; justify-content: space-between; margin-bottom: 2px; font-size: 11px; font-weight: 700; }
            .r-lbl { color: #000; flex-shrink: 0; margin-right: 6px; white-space: nowrap; font-weight: 700; }
            .r-val { text-align: right; font-weight: 700; word-break: break-word; }
            .r-item-name { flex: 1; padding-right: 4px; word-break: break-word; font-weight: 700; }
            .r-item-amt { white-space: nowrap; font-weight: 900; }
            .r-items-hdr { display: flex; justify-content: space-between; font-weight: 900; border-bottom: 1.5px solid #000; padding-bottom: 3px; margin-bottom: 4px; font-size: 11px; letter-spacing: 0.5px; }
            .r-total { display: flex; justify-content: space-between; font-weight: 900; font-size: 12px; margin-top: 4px; padding-top: 4px; border-top: 1.5px solid #000; }
            .r-green  { color: #000; }
            .r-red    { color: #000; }
            .r-small  { font-size: 10px; color: #000; font-weight: 700; }
            strong { font-weight: 900; }
            .r-feed { height: 25mm; width: 100%; }
          </style>
        </head>
        <body>
          <div class="receipt-wrap">
            <div class="r-center">
              <div class="r-h3">${(labInfo as any).lab_name || "Health Lab"}</div>
              ${(labInfo as any).location   ? `<div class="r-h4">${(labInfo as any).location}</div>`        : ""}
              ${(labInfo as any).contact_no ? `<div class="r-h4">Ph: ${(labInfo as any).contact_no}</div>` : ""}
              ${authUser?.full_name ? `<div class="r-h4">User: ${authUser.full_name}</div>` : ""}
            </div>
            <div class="r-hr"></div>
            <div>
              <strong>${patientName}</strong><br/>
              <span class="r-lbl">ID: </span>${patientInfo?.P_code || "—"}<br/>
              <span class="r-lbl">Mob: </span>${patientInfo?.mobile || "—"}
              ${patientInfo?.age != null ? `<br/><span class="r-lbl">Age: </span>${patientInfo.age} ${patientInfo.age_type || "Yrs"}${patientInfo.gender ? " | " + patientInfo.gender : ""}` : ""}
            </div>
            <div class="r-hr"></div>
            <div class="r-row"><span class="r-lbl">Bill No:</span><span class="r-val">${billData.billnum || "—"}</span></div>
            <div class="r-row"><span class="r-lbl">Date:</span><span class="r-val">${formattedDate}</span></div>
            <div class="r-row"><span class="r-lbl">Ref:</span><span class="r-val">${billData.ref_prefix || ""} ${billData.reference || ""}</span></div>
            <div class="r-row"><span class="r-lbl">Payment:</span><span class="r-val">${billData.payment_method || "—"}</span></div>
            <div class="r-hr"></div>
            <div class="r-items-hdr"><span>DESCRIPTION</span><span>AMOUNT</span></div>
            ${itemsHtml || `<div style="text-align:center;color:#000;padding:6px 0;font-weight:700;">No items</div>`}
            <div class="r-hr"></div>
            <div class="r-row"><span class="r-lbl">Sub Total</span><span class="r-val">₹${subTotal.toFixed(2)}</span></div>
            ${homeVisit > 0 ? `<div class="r-row"><span class="r-lbl">Home Visit</span><span class="r-val">₹${homeVisit.toFixed(2)}</span></div>` : ""}
            <div class="r-total"><span>NET TOTAL</span><span>₹${netTotal.toFixed(2)}</span></div>
            ${paid > 0 ? `
              <div class="r-row" style="margin-top:4px">
                <span class="r-lbl">Paid (${billData.payment_method || ""})</span>
                <span class="r-val r-green">₹${paid.toFixed(2)}</span>
              </div>
              ${billData.reference_no ? `<div class="r-row"><span class="r-lbl">Ref No:</span><span class="r-val">${billData.reference_no}</span></div>` : ""}
              <div class="r-row">
                <span class="r-lbl">Balance</span>
                <span class="r-val ${balanceColor}">₹${balance.toFixed(2)}</span>
              </div>
            ` : ""}
            <div class="r-hr"></div>
            <div class="r-center r-small" style="margin-bottom:10px">Thank you for visiting us!</div>
            <div class="r-feed"></div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", fontFamily: "monospace" }}>
        <div className="spinner-border text-primary" />
        <p style={{ marginTop: 10, marginLeft: 10 }}>Loading receipt...</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "red", fontFamily: "monospace" }}>
        ⚠ {error || "No data"}
      </div>
    );
  }

  const {
    labInfo, patientInfo, billData, subTotal, netTotal, paid, balance,
    homeVisit, patientName,
  } = buildReceiptData(data);

  const displayItems = mergeDisplayItems(data);

  const S = {
    page:       { minHeight: "100vh", display: "flex", flexDirection: "column" as const, alignItems: "center", padding: "20px", fontFamily: "'Courier New', Courier, monospace" },
    receipt:    { background: "white", width: "280px", padding: "14px 12px", border: "1px solid #ccc", fontSize: "11px", lineHeight: "1.7", color: "#222", fontWeight: 700, marginBottom: "10px" },
    center:     { textAlign: "center" as const },
    h3:         { fontSize: "13px", fontWeight: 900 as const, margin: "0 0 2px 0", textTransform: "uppercase" as const, color: "#222", letterSpacing: "0.3px" },
    h4:         { fontSize: "10px", margin: "0 0 2px 0", fontWeight: 700 as const, color: "#333" },
    hr:         { border: "none", borderTop: "1px dashed #777", margin: "6px 0" },
    row:        { display: "flex", justifyContent: "space-between", marginBottom: "2px", fontSize: "11px", color: "#222", fontWeight: 700 } as React.CSSProperties,
    lbl:        { color: "#555", flexShrink: 0, marginRight: "6px", whiteSpace: "nowrap" as const, fontWeight: 700 },
    val:        { textAlign: "right" as const, fontWeight: 700, wordBreak: "break-word" as const },
    iName:      { flex: 1, paddingRight: "4px", wordBreak: "break-word" as const, color: "#222", fontWeight: 700 },
    iAmt:       { whiteSpace: "nowrap" as const, fontWeight: 900 as const },
    iHdr:       { display: "flex", justifyContent: "space-between", fontWeight: 900 as const, borderBottom: "1px solid #ccc", paddingBottom: "3px", marginBottom: "4px", fontSize: "10px" } as React.CSSProperties,
    total:      { display: "flex", justifyContent: "space-between", fontWeight: 900 as const, fontSize: "12px", marginTop: "4px", paddingTop: "4px", borderTop: "1px solid #333" } as React.CSSProperties,
    actionBar:  { display: "flex", gap: "8px", marginTop: "14px", justifyContent: "center" },
    btnPrint:   { padding: "7px 20px", background: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontFamily: "sans-serif" },
    btnBack:    { padding: "7px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "13px", fontFamily: "sans-serif" },
  };

  return (
    <div style={S.page}>
      <div>
        <div style={S.receipt}>
          <div style={S.center}>
            <div style={S.h3}>{(labInfo as any).lab_name || "Health Lab"}</div>
            {(labInfo as any).location   && <div style={S.h4}>{(labInfo as any).location}</div>}
            {(labInfo as any).contact_no && <div style={S.h4}>Ph: {(labInfo as any).contact_no}</div>}
            {authUser?.full_name && <div style={S.h4}>User: {authUser.full_name}</div>}
          </div>
          <hr style={S.hr} />

          <div style={{ fontSize: "11px", color: "#222", lineHeight: "1.7", fontWeight: 700 }}>
            <strong style={{ fontWeight: 900 }}>{patientName}</strong><br />
            <span style={S.lbl}>ID: </span>{patientInfo?.P_code || "—"}<br />
            <span style={S.lbl}>Mob: </span>{patientInfo?.mobile || "—"}
            {patientInfo?.age != null && (<><br /><span style={S.lbl}>Age: </span>{patientInfo.age} {patientInfo.age_type || "Yrs"}{patientInfo.gender ? ` | ${patientInfo.gender}` : ""}</>)}
          </div>
          <hr style={S.hr} />

          <div style={S.row}><span style={S.lbl}>Bill No:</span><span style={S.val}>{billData.billnum || "—"}</span></div>
          <div style={S.row}><span style={S.lbl}>Date:</span><span style={S.val}>{formatDateIST(billData.bill_date)}</span></div>
          <div style={S.row}><span style={S.lbl}>Ref:</span><span style={S.val}>{billData.ref_prefix || ""} {billData.reference || ""}</span></div>
          <div style={S.row}><span style={S.lbl}>Payment:</span><span style={S.val}>{billData.payment_method || "—"}</span></div>
          <hr style={S.hr} />

          <div style={S.iHdr}><span>DESCRIPTION</span><span>AMOUNT</span></div>
          {displayItems.length === 0 ? (
            <div style={{ textAlign: "center", color: "#999", padding: "6px 0", fontSize: "11px" }}>No items</div>
          ) : (
            displayItems.map((item, idx) => (
              <div key={idx} style={S.row}>
                <span style={S.iName}>{item.name || "—"}</span>
                <span style={S.iAmt}>₹{item.amount.toFixed(2)}</span>
              </div>
            ))
          )}
          <hr style={S.hr} />

          <div style={S.row}><span style={S.lbl}>Sub Total</span><span style={S.val}>₹{subTotal.toFixed(2)}</span></div>
          {homeVisit > 0 && <div style={S.row}><span style={S.lbl}>Home Visit</span><span style={S.val}>₹{homeVisit.toFixed(2)}</span></div>}
          <div style={S.total}><span>NET TOTAL</span><span>₹{netTotal.toFixed(2)}</span></div>
          {paid > 0 && (<>
            <div style={{ ...S.row, marginTop: "4px" }}>
              <span style={S.lbl}>Paid ({billData.payment_method || ""})</span>
              <span style={{ ...S.val, color: "green" }}>₹{paid.toFixed(2)}</span>
            </div>
            {billData.reference_no && <div style={S.row}><span style={S.lbl}>Ref No:</span><span style={S.val}>{billData.reference_no}</span></div>}
            <div style={S.row}>
              <span style={S.lbl}>Balance</span>
              <span style={{ ...S.val, color: balance > 0 ? "red" : "green" }}>₹{balance.toFixed(2)}</span>
            </div>
          </>)}
          <hr style={S.hr} />
          <div style={{ ...S.center, fontSize: "10px", color: "#555", fontWeight: 700 }}>Thank you for visiting us!</div>
          <div style={{ height: "24px" }} />
        </div>

        <div style={S.actionBar}>
          <button style={S.btnPrint} onClick={handlePrint}>🖨 Print Receipt</button>
          <button style={S.btnBack}  onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>
    </div>
  );
};

export default BillingPosReceipt;